import React, { createContext, useState, useContext } from 'react';
import { PointOfSale } from '../types/types';

interface User {
  phone_number: string;
  type: string;
  first_name: string;
  last_name: string;
  individual_entrepreneur: string;
  points_of_sale: PointOfSale[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  pointOfSale: PointOfSale | null;
  setPointOfSale: (pos: PointOfSale) => void;
  user: User | null;
  login: (response: any) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  pointOfSale: null,
  setPointOfSale: () => {},
  user: null,
  login: () => {},
  logout: () => {}
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize state from localStorage
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('token');
  });

  const [pointOfSaleState, setPointOfSaleState] = useState<PointOfSale | null>(() => {
    const savedPos = localStorage.getItem('pointOfSale');
    return savedPos ? JSON.parse(savedPos) : null;
  });

  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const login = (response: any) => {
    localStorage.setItem('token', response.access_token);
    localStorage.setItem('refreshToken', response.refresh_token);
    localStorage.setItem('user', JSON.stringify({
      phone_number: response.phone_number,
      type: response.type,
      first_name: response.first_name,
      last_name: response.last_name,
      individual_entrepreneur: response.individual_entrepreneur,
      points_of_sale: response.points_of_sale
    }));
    
    setIsAuthenticated(true);
    setUser({
      phone_number: response.phone_number,
      type: response.type,
      first_name: response.first_name,
      last_name: response.last_name,
      individual_entrepreneur: response.individual_entrepreneur,
      points_of_sale: response.points_of_sale
    });

    // Set initial point of sale if available
    if (response.points_of_sale && response.points_of_sale.length > 0) {
      handleSetPointOfSale(response.points_of_sale[0]);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('pointOfSale');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setPointOfSaleState(null);
    setUser(null);
  };

  const handleSetPointOfSale = (pos: PointOfSale) => {
    localStorage.setItem('pointOfSale', JSON.stringify(pos));
    setPointOfSaleState(pos);
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      pointOfSale: pointOfSaleState, 
      setPointOfSale: handleSetPointOfSale,
      user,
      login, 
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 